/*
documentation:
This component can be used for searching internal or external users.
Once user types a name of a user and hits enter, he is presented with list of users.
On seleting user, caller component received the date via
onUserSelectionChange.
*/

import React, {useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import _ from 'lodash';
import styled from 'styled-components';
import {SVGICONS} from '@ey/icons';
import {UserLookupType} from '../../utils/uiconstants';
import {labels} from '../../utils/utils';
import ReactSelect from '../SelectDropdown/SelectDropdown';
import {getAllUsers} from '../../../features/users/users';
import {MotifErrorMessage, MotifIcon} from '@ey-xd/motif-react';
import {
	contentIcClear24px,
	actionIcSearch24px
} from '@ey-xd/motif-react/assets/icons';
import {Ellipse} from '@ey/canvascoreservices';

export default function UserSearch(props) {
	const [selectedUser, setSelectedUser] = useState('');
	const [userOptions, setUserOptions] = useState([]);
	const [isUserOptionsOpen, setIsUserOptionsOpen] = useState(false);
	const [defaultValue, setDefaultValue] = useState('');
	const [searchDisabled, setSearchDisabled] = useState(false);
	const [startShowingResults, setStartShowingResults] = useState(false);
	const [callFailed, setCallFailed] = useState(false);
	const [failureError, setFailureError] = useState('');
	const [showValidations, setShowValidations] = useState(false);
	const [ignoreFirst, setIgnoreFirst] = useState(true);
	const [searchText, setSearchText] = useState('');

	const search = useSelector((store) => store.user.search?.byId);
	const searchUsers = useDispatch();

	const showValidationsForSearch = () => {
		if (selectedUser && selectedUser !== 'default') setShowValidations(false);
		else setShowValidations(true);
	};

	const enterKeyPress = (e) => {
		setStartShowingResults(true);
		if (e.target.value === '') {
			setUserOptions([]);
		} else {
			if (
				props.lookupType === UserLookupType.External &&
				!validateEmail(e.target.value)
			) {
				setShowValidations(true);
				return;
			}
			getUserSearchResults(e.target.value, props.lookupType || 1);
		}
	};

	const onSearchClick = (event) => {
		event.preventDefault();
		setStartShowingResults(true);
		if (searchText === '') {
			setUserOptions([]);
		} else {
			if (
				props.lookupType === UserLookupType.External &&
				!validateEmail(searchText)
			) {
				setShowValidations(true);
				return;
			}
			getUserSearchResults(searchText, props.lookupType || 1);
		}
	};

	const formatOptionLabel = ({value, label}) => (
		<section>
			<section className="userSearchLabel">
				<Ellipse content={label} tooltip={label} noOfLines={1} />
			</section>
			<section className="userSearchValue">
				<Ellipse content={value} tooltip={value} noOfLines={1} />
			</section>
		</section>
	);

	function validateEmail(emailField) {
		const reg = /^([A-Za-z0-9_\-\.\'])+\@([A-Za-z0-9_\-\.])+\.([A-Za-z]{2,63})$/;
		return reg.test(emailField);
	}

	const keyPress = (e) => {
		setShowValidations(false); //clear validations
		setSearchText(e.target.value);
		if (e.keyCode === 13 && !isUserOptionsOpen) {
			//enter press
			enterKeyPress(e);
		} else if (e.keyCode !== 38 && e.keyCode !== 40) {
			setIsUserOptionsOpen(false);
			setUserOptions([]);
		}
	};

	const clearText = () => {
		setSearchDisabled(false);
		setCallFailed(false);
		setFailureError('');
		setDefaultValue('');
		setSelectedUser('');
		setIsUserOptionsOpen(false);
		if (props.onUserClear) {
			props.onUserClear();
		}
	};

	const getSingleUserOption = (user) => {
		return {
			label: user.displayName,
			value: user.email,
			allProps: user
		};
	};

	const setLoading = (isLoading) => {
		props.setLoading && props.setLoading(isLoading);
	};

	const getUserSearchResults = (searchString, userLookUpType) => {
		setCallFailed(false);
		setFailureError('');
		setLoading(true);
		setSearchDisabled(true);

		searchUsers(
			getAllUsers({
				searchString,
				userLookUpType,
				isAdminSearch: props.IsAdminSearch
			})
		)
			.then(() => {
				setSearchDisabled(false);
				setLoading(false);
				setUserSearchFocus();
			})
			.catch((e) => {
				setFailureError('Failure:' + e);
				setCallFailed(true);
				setSearchDisabled(false);
				setLoading(false);
				setUserOptions([]);
			});
	};

	const setUserSearchFocus = () => {
		const ddl = document.querySelector('#usesearch');
		ddl.querySelector('.react-select__input').children[0].focus();
	};

	useEffect(() => {
		document.getElementById('usesearch').querySelector('input').maxLength =
			props.maxSearchLength && typeof props.maxSearchLength === 'number'
				? props.maxSearchLength
				: 100;
	}, []);

	useEffect(() => {
		const handleOnDocumentClick = (event) => {
			if (
				isUserOptionsOpen &&
				!document
					.querySelector('.userfield-row .select-group')
					.contains(event.target)
			) {
				setIsUserOptionsOpen(false);
			}
		};

		document.addEventListener('click', handleOnDocumentClick);
		return () => {
			document.removeEventListener('click', handleOnDocumentClick);
		};
	}, [isUserOptionsOpen]);

	useEffect(() => {
		if (startShowingResults) {
			setIsUserOptionsOpen(true);
			if (search) {
				setUserOptions(_.map(Object.values(search), getSingleUserOption));
			} else setUserOptions([]);
			setStartShowingResults(false);
		}
	}, [search]);

	useEffect(() => {
		if (!ignoreFirst) {
			showValidationsForSearch();
		} else {
			setIgnoreFirst(false);
		}
	}, [props.showValidations]);

	return (
		<StyledUserSearch className="userfield-row nameandemail">
			{callFailed ? (
				<section className="error"> {failureError} </section>
			) : null}
			<section
				className={`user-selectbox ${
					props.user?.isError ? 'userSelectError' : ''
				}`}
			>
				<StyledSelectBox
					className={'select-group ' + (searchDisabled ? ' inputDisabled' : '')}
					formLabel={
						props.lookupType === UserLookupType.External
							? labels.emailForExternal
							: labels.nameOrEmail
					}
					noOptionsMessage={() => labels.noRecord}
					defaultValue={defaultValue}
					options={userOptions}
					isSearchable
					searchInLabelOrValue
					isClearable={false}
					id="usesearch"
					errorMessage={
						props.lookupType === UserLookupType.External
							? labels.inValidEmail
							: labels.seachInputRequired
					}
					showErrorMessage={showValidations}
					onChange={(option) => {
						if (option === null) {
							setShowValidations(false);
							setDefaultValue('');
							setSearchDisabled(false);
						} else {
							let value = props?.showNameAsValue ? option.label : option.value;
							let opt = {
								label: '',
								value: value,
								allProps: option.allProps
							};
							setIsUserOptionsOpen(false);
							setDefaultValue([opt.label]);
							setSearchDisabled(true);
							setUserOptions([]);
							setSelectedUser(opt);
							if (props.onUserSelectionChange) {
								props.onUserSelectionChange(opt);
							}
						}
					}}
					isDisabled={searchDisabled}
					value={selectedUser}
					onInputKeyDown={keyPress}
					menuIsOpen={isUserOptionsOpen}
					formatOptionLabel={formatOptionLabel}
					onBlur={() => setIsUserOptionsOpen(false)}
				/>
				{props.userError ||
					(props.requiredError && (
						<MotifErrorMessage>
							{props.userError
								? 'Delegate already added'
								: 'This information is required'}
							;
						</MotifErrorMessage>
					))}
				{selectedUser && (
					<span
						title={labels.clear}
						className="pull-left clearall"
						onClick={clearText}
					>
						{!props.isClearable ? (
							<SVGICONS
								styleName="medium"
								name={'close'}
								hoverText={labels.clearAllValues}
								alt="Clear"
							/>
						) : (
							<MotifIcon src={contentIcClear24px} />
						)}
					</span>
				)}
				{!selectedUser && props.showSearch && (
					<span
						title={labels.search}
						className="pull-left clearall"
						onClick={onSearchClick}
					>
						<MotifIcon src={actionIcSearch24px} />
					</span>
				)}
			</section>
			<section className="enterdetail-description">
				{props.user?.isError ? (
					<MotifErrorMessage>{props.user.errorMessage}</MotifErrorMessage>
				) : props.showNameAsValue ? (
					labels.userSearchPlaceholder
				) : (
					props.lookupType === UserLookupType.External &&
					labels.userSearchPlaceholderForExternal
				)}
			</section>
		</StyledUserSearch>
	);
}

const StyledUserSearch = styled.section`
	width: calc(100% - 20rem);
	.error {
		color: red;
	}
	.user-selectbox {
		display: flex;
		width: 100%;
		&.userSelectError {
			.select-group {
				.react-select-container {
					.react-select__control {
						border-bottom: 0.1rem solid
							${(props) => props.theme.color[`red600`]};
					}
				}
			}
		}
		.clearall {
			line-height: 4.5rem;
			margin: 0 1.5rem;

			svg {
				vertical-align: middle;
			}
		}

		.enterdetail-loading {
			height: 4.5rem;
			float: left;

			.motif-loader-container.nameemailloader {
				padding-top: 1.6rem;
			}
		}
	}
	.enterdetail-description {
		display: inline-block;
		width: 100%;
		font-size: 1.2rem;
		padding-left: 1rem;
		color: ${(props) => props.theme.color[`grey400`]};
		.motif-error-message {
			color: ${(props) => props.theme.color[`red600`]};
			margin-top: 0.8rem;
		}
	}
	svg {
		fill: ${(props) => props.theme.color[`black`]};
	}
`;

const StyledSelectBox = styled(ReactSelect)`
	font-size: 1.4rem;
	color: ${(props) => props.theme.color[`grey400`]};

	label {
		font-size: 1.4rem;
	}

	.react-select__input {
		color: ${(props) => props.theme.color[`black`]};
	}

	&.inputDisabled > *,
	&.inputDisabled .react-select__single-value {
		color: ${(props) => props.theme.color[`grey300`]} !important;
	}

	&.inputDisabled.select-disabled label {
		color: ${(props) => props.theme.color[`grey300`]} !important;
	}

	&.floating-label.inputDisabled.hideInput label {
		color: ${(props) => props.theme.color[`grey500`]} !important;
	}

	.react-select-container {
		.react-select__control {
			.react-select__value-container {
				.react-select__single-value {
					.userSearchLabel {
						display: none;
					}
				}
			}
		}
		.react-select__menu {
			z-index: 101;
			.react-select__menu-list {
				max-height: 25rem;
				.userSearchValue,
				.userSearchLabel {
					line-height: normal;
				}
			}
		}
	}
`;
